import { useId } from "react"
import Icon from "~/components/ui/Icon"
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice"
import type {
    Filter,
    FilterToggle,
    FilterToggleValue,
    ProductListingPage,
} from "@decocms/apps-commerce/types"
import { parseRange } from "@decocms/apps-commerce/utils/filters"
import sortUrl from "~/sdk/sortUrl"
import { useSignal } from "~/sdk/signal"
import FilterItemCollapse from "~/components/search/FilterItemCollapse"
import { SITE } from "~/constants"

interface Props {
    filters: ProductListingPage["filters"]
    categoryName: string
    isSearchPage: boolean
    url: string
    isMobile?: boolean
    isPerformanceTestPage: boolean
    paginateFilterOnMobile?: boolean
    brandPage?: boolean
}

interface FilterComponentProps {
    filter: FilterToggle
    isMobile?: boolean
    isPerformanceTestPage: boolean
    brandPage?: boolean
}

const EXCLUDE_NO_FOLLOW = ["Categories", "Brands", "Cor"]
const isToggle = (filter: Filter): filter is FilterToggle => filter["@type"] === "FilterToggle"

function ValueItem(
    { url, selected, label, quantity, nofollow, isMobile = true, isPerformanceTestPage, brandPage }:
        & FilterToggleValue
        & {
            nofollow: boolean
            isMobile?: boolean
            isPerformanceTestPage: boolean
            brandPage?: boolean
        },
) {
    const id = useId()

    return (
        <li title={label} className="group/check">
            <a
                href={brandPage ? url.toLocaleLowerCase() : sortUrl(url)}
                className="flex items-center gap-2"
                rel={nofollow ? "nofollow" : undefined}
            >
                {!isPerformanceTestPage && (
                    <div
                        aria-labelledby={id}
                        role="checkbox"
                        aria-checked={selected}
                        className="border border-gray-200 text-transparent aria-checked:text-black aria-checked:stroke-black group-hover/check:text-black aria-checked:border-salmon group-hover/check:border-salmon no-animation h-5 w-5 flex justify-center items-center"
                    >
                        {(!isMobile || (isMobile && selected)) && (
                            <Icon id="Check" size={10} strokeWidth={20} />
                        )}
                    </div>
                )}

                <span
                    id={id}
                    className={`text-sm ${
                        isPerformanceTestPage && selected ? "text-salmon-600 font-bold" : ""
                    }`}
                >
                    {label}
                    {quantity > 0 ? ` (${quantity})` : ""}
                </span>
            </a>
        </li>
    )
}

function FilterValuesPaginated(
    { keyFilter: key, values, isMobile, isPerformanceTestPage, brandPage }: FilterToggle & {
        isMobile?: boolean
        isPerformanceTestPage: boolean
        brandPage?: boolean
        keyFilter: string
    },
) {
    const currentPage = useSignal(0)
    const itemsPerPage = 10
    const totalPages = Math.ceil(values.length / itemsPerPage)
    const accumulatedValues = useSignal(values.slice(0, itemsPerPage))

    const handleNextPage = (e: React.MouseEvent) => {
        e.preventDefault()

        if (currentPage.value < totalPages - 1) {
            currentPage.value = currentPage.value + 1
            const newValues = values.slice(
                currentPage.value * itemsPerPage,
                (currentPage.value + 1) * itemsPerPage,
            )
            accumulatedValues.value = [...accumulatedValues.value, ...newValues]
        }
    }

    const handlePreviousPage = (e: React.MouseEvent) => {
        e.preventDefault()
        if (currentPage.value > 0) {
            currentPage.value = currentPage.value - 1
            accumulatedValues.value = values.slice(0, (currentPage.value + 1) * itemsPerPage)
        }
    }

    return (
        <div>
            <ul className="flex gap-3 flex-col">
                {accumulatedValues.value.map((item) => {
                    if (key === "price") {
                        const range = parseRange(item.value)

                        return (
                            range && (
                                <ValueItem
                                    key={item.value + item.label}
                                    {...item}
                                    label={`${formatPrice(range.from)} - ${formatPrice(range.to)}`}
                                    nofollow={!EXCLUDE_NO_FOLLOW.includes(key)}
                                    isMobile={isMobile}
                                    isPerformanceTestPage={isPerformanceTestPage}
                                    brandPage={brandPage}
                                />
                            )
                        )
                    }

                    return (
                        <ValueItem
                            key={item.value + item.label}
                            nofollow={!EXCLUDE_NO_FOLLOW.includes(key)}
                            {...item}
                            isMobile={isMobile}
                            isPerformanceTestPage={isPerformanceTestPage}
                            brandPage={brandPage}
                        />
                    )
                })}
            </ul>
            <div className="flex justify-between mt-4">
                {totalPages > 1 && currentPage.value < totalPages - 1 && (
                    <button
                        className="font-bold text-base text-salmon p-0 pb-1 min-h-0 h-5 w-full flex items-center justify-start"
                        onClick={handleNextPage}
                    >
                        <span className="underline underline-offset-4">
                            Ver Mais [+]
                        </span>
                    </button>
                )}

                {currentPage.value > 0 && (
                    <button
                        className="font-bold text-base text-salmon p-0 pb-1 min-h-0 h-5 w-full flex items-center justify-start"
                        onClick={handlePreviousPage}
                        disabled={currentPage.value === 0}
                    >
                        <span className="underline underline-offset-4">
                            Ver Menos [-]
                        </span>
                    </button>
                )}
            </div>
        </div>
    )
}

export function FilterValues(
    { keyFilter: key, values, isMobile, isPerformanceTestPage, brandPage, label }: FilterToggle & {
        isMobile?: boolean
        isPerformanceTestPage: boolean
        brandPage?: boolean
        keyFilter: string
    },
) {
    const firstValues = values.slice(0, 5)
    const collapseValues = values.slice(5)
    const id = useId()

    return (
        <ul className="flex gap-3 flex-col">
            {firstValues.map((item) => {
                if (key === "price") {
                    const range = parseRange(item.value)

                    return (
                        range && (
                            <ValueItem
                                key={item.value + item.label}
                                {...item}
                                label={`${formatPrice(range.from)} - ${formatPrice(range.to)}`}
                                nofollow={!EXCLUDE_NO_FOLLOW.includes(key)}
                                isMobile={isMobile}
                                isPerformanceTestPage={isPerformanceTestPage}
                                brandPage={brandPage}
                            />
                        )
                    )
                }

                return (
                    <ValueItem
                        key={item.value + item.label}
                        nofollow={!EXCLUDE_NO_FOLLOW.includes(key)}
                        {...item}
                        isMobile={isMobile}
                        isPerformanceTestPage={isPerformanceTestPage}
                        brandPage={brandPage}
                    />
                )
            })}

            {!!collapseValues.length && (
                <FilterItemCollapse id={id}>
                    {collapseValues.map((item) => {
                        if (key === "price") {
                            const range = parseRange(item.value)

                            return (
                                range && (
                                    <ValueItem
                                        key={item.value + item.label}
                                        {...item}
                                        label={`${formatPrice(range.from)} - ${
                                            formatPrice(range.to)
                                        }`}
                                        nofollow={!EXCLUDE_NO_FOLLOW.includes(key)}
                                        isMobile={isMobile}
                                        isPerformanceTestPage={isPerformanceTestPage}
                                        brandPage={brandPage}
                                    />
                                )
                            )
                        }

                        return (
                            <ValueItem
                                key={item.value + item.label}
                                {...item}
                                nofollow={!EXCLUDE_NO_FOLLOW.includes(key)}
                                isMobile={isMobile}
                                isPerformanceTestPage={isPerformanceTestPage}
                                brandPage={brandPage}
                            />
                        )
                    })}
                </FilterItemCollapse>
            )}
        </ul>
    )
}

const EXCLUDE = ["Departments"]
const translate = {
    Brands: "Marca",
    PriceRanges: "Faixa de Preço",
} as Record<string, string>

function Filters(
    {
        filters: f,
        categoryName,
        isSearchPage,
        url,
        isMobile,
        isPerformanceTestPage,
        paginateFilterOnMobile,
        brandPage,
    }: Props,
) {
    if (!f.length) return null

    // `url` chega relativo nesta migração (no original era a URL absoluta da
    // request), e um base relativo faz `new URL` lançar "Invalid base URL".
    const URLi = new URL(url ?? "/", SITE.URL)

    let filters: FilterToggle[]

    if (isSearchPage) {
        filters = f
            .filter(isToggle)
            .filter((filter) =>
                filter.values.length > 0 &&
                !["Departments", "Categories", "Brands", "PriceRanges"].includes(filter.key)
            )
            ?.map(({ key, values, ...filter }) => {
                const keyUrl = key.split(" ").join("-")
                return {
                    ...filter,
                    key,
                    values: values.flatMap(({ value, children }) => {
                        return (children?.values as FilterToggleValue[])
                            ?.map(({ url: itemUrl, ...item }) => {
                                const newUrl = new URL(`/${keyUrl}/${value}${itemUrl}`, URLi)
                                newUrl.searchParams.set("map", "c,c,c")

                                return { ...item, url: `${newUrl.pathname}${newUrl.search}` }
                            })
                    })
                        .filter(Boolean)
                        .sort((a, b) => a.label.localeCompare(b.label)),
                }
            })
    } else {
        filters = f
            .filter(isToggle)
            .filter(
                (i) =>
                    Object.keys(i).length > 0 &&
                    i.values.length > 0 &&
                    !EXCLUDE.includes(i.key) &&
                    !(i.key === "Brands" && isSearchPage),
            )
            .map((i) => {
                if (i.key === "Categories" || i.key === "Brands" || isSearchPage) {
                    i.values = [...i.values].sort((a, b) => a.label.localeCompare(b.label))
                }

                return i
            })
            .map((i) => {
                if (translate[i.key]) {
                    i.label = translate[i.key]
                } else {
                    if (i.key === "Categories") {
                        i.label = categoryName
                    }
                }

                return i
            })
    }

    const brandsFilter = filters.findIndex((i) => i.key === "Brands")
    const priceFilter = filters.findIndex((i) => i.key === "PriceRanges")

    if (filters.length && brandsFilter !== -1) {
        filters.splice(0, 0, filters.splice(brandsFilter, 1)[0])
    }

    if (filters.length && priceFilter !== -1) {
        filters.push(filters.splice(priceFilter, 1)[0])
    }

    const category = filters.find((i) => i.label === categoryName)

    if (category) {
        filters = [category, ...filters.filter((i) => i.label !== categoryName)]
    }

    const sort = URLi.searchParams.get("O")

    if (sort) {
        for (const filter of filters) {
            for (const value of filter.values) {
                const u = new URL(URLi.origin + value.url)

                u.searchParams.set("O", sort)

                // Keep the URL relative — `.href` would include the
                // hard-coded miess.com.br origin and break navigation.
                value.url = u.pathname + u.search

                if (value.children) {
                    if (value.children.values) continue

                    const children = Array.isArray(value.children)
                        ? value.children
                        : [value.children]

                    for (const child of children) {
                        const u = new URL(URLi.origin + child.url)

                        u.searchParams.set("O", sort)

                        child.url = u.pathname + u.search
                    }
                }
            }
        }
    }

    const FilterComponent = (
        { filter, isMobile, isPerformanceTestPage, brandPage }: FilterComponentProps,
    ) => {
        const FilterComponentToRender = paginateFilterOnMobile && isMobile
            ? FilterValuesPaginated
            : FilterValues

        const filterKey = filter.key

        return (
            <FilterComponentToRender
                {...filter}
                isMobile={isMobile}
                isPerformanceTestPage={isPerformanceTestPage}
                brandPage={brandPage}
                keyFilter={filterKey}
            />
        )
    }

    return (
        <div className="divide-y divide-[#feb7d7] divide-solid">
            {filters.map((filter) => {
                return (
                    <div key={filter.key} className="collapse relative rounded-none py-2">
                        <input
                            type="checkbox"
                            id={`${filter.key}-toggle`}
                            className="fixed hidden peer"
                            defaultChecked={true}
                        />
                        <label
                            htmlFor={`${filter.key}-toggle`}
                            className="swap peer-checked:swap-active collapse-title font-black uppercase text-lg text-orange p-0 min-h-0 h-12 w-full flex items-center justify-start"
                        >
                            <h3 className="ml-5">{filter.label}</h3>
                            <Icon
                                id="Plus"
                                size={14}
                                strokeWidth={2}
                                className="swap-off absolute left-0 top-1/2 -translate-y-1/2"
                            />
                            <Icon
                                id="Minus"
                                size={14}
                                strokeWidth={2}
                                className="swap-on absolute left-0 top-1/2 -translate-y-1/2"
                            />
                        </label>
                        <div className="collapse-content p-0 pt-2 text-sm text-black">
                            {filter.label === "Marca" || filter.key === "Categories" && brandPage
                                ? (
                                    <FilterComponent
                                        filter={filter}
                                        isMobile={isMobile}
                                        isPerformanceTestPage={isPerformanceTestPage}
                                        brandPage={brandPage}
                                    />
                                )
                                : (
                                    <FilterComponent
                                        filter={filter}
                                        isMobile={isMobile}
                                        isPerformanceTestPage={isPerformanceTestPage}
                                    />
                                )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

export default Filters
